/**
 * 逐次（ストリーミング）SHA-1。
 *
 * 【なぜ自前実装が要るのか】
 * Box の分割アップロードは commit 時に**ファイル全体の SHA-1** を要求する。
 * ところが Web Crypto の crypto.subtle.digest() は一括APIしか無く、
 * 50GB のファイルをメモリに載せることはできない。
 * そこで、パートを読みながら 1 パスで更新できる逐次実装を持つ。
 *
 * 正しさは test/sha1.test.ts で crypto.subtle.digest('SHA-1') と突き合わせている。
 * （Web Crypto 自体は SHA-1 の digest に対応しているので、小さいデータで検証できる）
 */

const BLOCK_SIZE = 64

export class Sha1 {
  private h0 = 0x67452301
  private h1 = 0xefcdab89
  private h2 = 0x98badcfe
  private h3 = 0x10325476
  private h4 = 0xc3d2e1f0

  /** 64バイト境界に満たない端数を保持するバッファ。 */
  private readonly buffer = new Uint8Array(BLOCK_SIZE)
  private bufferLength = 0
  private totalLength = 0

  private readonly block = new Uint32Array(80)

  update(data: Uint8Array): this {
    this.totalLength += data.length
    let offset = 0

    // 前回の端数を埋める
    if (this.bufferLength > 0) {
      const need = BLOCK_SIZE - this.bufferLength
      const take = Math.min(need, data.length)
      this.buffer.set(data.subarray(0, take), this.bufferLength)
      this.bufferLength += take
      offset = take
      if (this.bufferLength === BLOCK_SIZE) {
        this.processBlock(this.buffer, 0)
        this.bufferLength = 0
      }
    }

    // 64バイト単位で処理する
    while (offset + BLOCK_SIZE <= data.length) {
      this.processBlock(data, offset)
      offset += BLOCK_SIZE
    }

    // 余りを保持
    if (offset < data.length) {
      this.buffer.set(data.subarray(offset), 0)
      this.bufferLength = data.length - offset
    }
    return this
  }

  /** 確定させてダイジェストを返す。呼んだ後にこのインスタンスは使わない。 */
  digest(): Uint8Array {
    const bitLength = this.totalLength * 8

    // パディング: 0x80 の後、長さフィールド(8バイト)の手前まで 0 を詰める
    const padLength = this.bufferLength < 56 ? 56 - this.bufferLength : 120 - this.bufferLength
    const tail = new Uint8Array(padLength + 8)
    tail[0] = 0x80

    // 長さは 64bit ビッグエンディアン。50GB でも 2^53 に収まるので分割して書く。
    const high = Math.floor(bitLength / 0x100000000)
    const low = bitLength >>> 0
    const view = new DataView(tail.buffer)
    view.setUint32(padLength, high, false)
    view.setUint32(padLength + 4, low, false)

    this.update(tail)

    const out = new Uint8Array(20)
    const outView = new DataView(out.buffer)
    outView.setUint32(0, this.h0 >>> 0, false)
    outView.setUint32(4, this.h1 >>> 0, false)
    outView.setUint32(8, this.h2 >>> 0, false)
    outView.setUint32(12, this.h3 >>> 0, false)
    outView.setUint32(16, this.h4 >>> 0, false)
    return out
  }

  private processBlock(data: Uint8Array, offset: number): void {
    const w = this.block

    for (let i = 0; i < 16; i++) {
      const j = offset + i * 4
      w[i] =
        ((data[j]! << 24) | (data[j + 1]! << 16) | (data[j + 2]! << 8) | data[j + 3]!) >>> 0
    }
    for (let i = 16; i < 80; i++) {
      const x = w[i - 3]! ^ w[i - 8]! ^ w[i - 14]! ^ w[i - 16]!
      w[i] = ((x << 1) | (x >>> 31)) >>> 0
    }

    let a = this.h0
    let b = this.h1
    let c = this.h2
    let d = this.h3
    let e = this.h4

    for (let i = 0; i < 80; i++) {
      let f: number
      let k: number
      if (i < 20) {
        f = (b & c) | (~b & d)
        k = 0x5a827999
      } else if (i < 40) {
        f = b ^ c ^ d
        k = 0x6ed9eba1
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d)
        k = 0x8f1bbcdc
      } else {
        f = b ^ c ^ d
        k = 0xca62c1d6
      }
      const temp = (((a << 5) | (a >>> 27)) + f + e + k + w[i]!) >>> 0
      e = d
      d = c
      c = ((b << 30) | (b >>> 2)) >>> 0
      b = a
      a = temp
    }

    this.h0 = (this.h0 + a) >>> 0
    this.h1 = (this.h1 + b) >>> 0
    this.h2 = (this.h2 + c) >>> 0
    this.h3 = (this.h3 + d) >>> 0
    this.h4 = (this.h4 + e) >>> 0
  }
}

/** Box の Digest ヘッダ形式（SHA-1 の生バイナリを Base64）。hex の Base64 ではない。 */
export function toBoxDigest(sha1: Uint8Array): string {
  let binary = ''
  for (const b of sha1) binary += String.fromCharCode(b)
  return `sha=${btoa(binary)}`
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** 単発の SHA-1（パート単位のダイジェスト用）。 */
export async function sha1Once(data: Uint8Array): Promise<Uint8Array> {
  // 小さいデータは Web Crypto の方が速い
  const buffer = await crypto.subtle.digest('SHA-1', data as unknown as BufferSource)
  return new Uint8Array(buffer)
}
